import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  SubscriptionDto,
  CreateTenantPayload,
  CreateSubscriptionPayload,
  TenantDto,
} from '../models/tenant.model';
import { BaseApiService } from '@app/core/api/base-api.service';
import { PageResponse } from '@app/core/api/api-response.interface';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly api = inject(BaseApiService);
  private readonly apiVersion = 'v1';
  private readonly resourcePath = `${this.apiVersion}/tenants`;

  private mapApiTenant(api: TenantDto | unknown): TenantDto {
    const raw = (api ?? {}) as any;
    const mapSub = (s: any) => ({
      subscriptionUuid: s?.subscriptionUuid ?? s?.id ?? s?.uuid ?? '',
      tenantUuid: s?.tenantUuid ?? s?.tenantId ?? '',
      subscriptionName: s?.subscriptionName ?? s?.name ?? '',
      createdAt: String(s?.createdAt ?? s?.created_at ?? ''),
      updatedAt: String(s?.updatedAt ?? s?.updated_at ?? ''),
    });

    const subscriptions = Array.isArray(raw.subscriptions)
      ? (raw.subscriptions as any[]).map((s) => mapSub(s))
      : [];

    return {
      tenantUuid: raw?.tenantUuid ?? raw?.id ?? raw?.uuid ?? '',
      tenantName: raw?.tenantName ?? raw?.name ?? '',
      tenantDescription: raw?.tenantDescription ?? raw?.description ?? '',
      tenantAvatarUrl: raw?.tenantAvatarUrl ?? raw?.logoUrl ?? '',
      firstDayOfWeek: Number(raw?.firstDayOfWeek ?? 1),
      twoFactorRequired: raw?.twoFactorRequired === true,
      createdAt: String(raw?.createdAt ?? raw?.created_at ?? ''),
      updatedAt: String(raw?.updatedAt ?? raw?.updated_at ?? ''),
      subscriptions,
    } as TenantDto;
  }

  getTenants(
    page: number = 0,
    size: number = 20,
    tenantName?: string,
    includeSubscriptions?: boolean,
    filters?: Record<string, unknown[]>,
  ): Observable<readonly TenantDto[]> {
    type ApiTenant = TenantDto;
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (includeSubscriptions !== undefined) {
      params = params.set('includeSubscriptions', String(includeSubscriptions));
    }
    if (tenantName && tenantName.trim().length > 0) {
      params = params.set('search', tenantName.trim());
    }
    if (filters) {
      for (const [key, values] of Object.entries(filters)) {
        for (const value of values) {
          params = params.append(key, String(value));
        }
      }
    }
    return this.api.get<readonly ApiTenant[]>(this.resourcePath, { params }).pipe(
      map((arr) => (Array.isArray(arr) ? arr : [])),
      map((arr) => arr.map((t) => this.mapApiTenant(t))),
    );
  }

  getTenantsPage(
    page: number,
    size: number,
    search?: string,
    includeSubscriptions?: boolean,
    filters?: Record<string, unknown[]>,
  ): Observable<{ tenants: readonly TenantDto[]; totalElements: number }> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (includeSubscriptions !== undefined) {
      params = params.set('includeSubscriptions', String(includeSubscriptions));
    }
    if (search && search.trim().length > 0) {
      params = params.set('search', search.trim());
    }
    if (filters) {
      for (const [key, values] of Object.entries(filters)) {
        for (const value of values) {
          params = params.append(key, String(value));
        }
      }
    }
    return this.api.get<PageResponse<TenantDto>>(`${this.resourcePath}/page`, { params }).pipe(
      map((res) => {
        const content = Array.isArray(res?.content) ? res.content : [];
        return {
          tenants: content.map((t) => this.mapApiTenant(t)),
          totalElements:
            typeof res?.totalElements === 'number' ? res.totalElements : content.length,
        };
      }),
    );
  }

  getTenant(tenantId: string, includeSubscriptions?: boolean): Observable<TenantDto> {
    type ApiTenant = TenantDto;
    let params = new HttpParams();
    if (includeSubscriptions !== undefined) {
      params = params.set('includeSubscriptions', String(includeSubscriptions));
    }
    return this.api
      .get<ApiTenant>(`${this.resourcePath}/${tenantId}`, { params })
      .pipe(map((t) => this.mapApiTenant(t)));
  }

  createTenant(payload: CreateTenantPayload): Observable<TenantDto> {
    type ApiTenant = TenantDto;
    return this.api
      .post<ApiTenant>(this.resourcePath, payload)
      .pipe(map((t) => this.mapApiTenant(t)));
  }

  updateTenant(
    tenantId: string,
    changes: {
      tenantName: string;
      tenantDescription: string;
      tenantAvatarUrl: string;
      firstDayOfWeek: number;
      twoFactorRequired: boolean;
    },
  ): Observable<TenantDto> {
    type ApiTenant = TenantDto;
    return this.api
      .put<ApiTenant>(`${this.resourcePath}/${tenantId}`, changes)
      .pipe(map((t) => this.mapApiTenant(t)));
  }

  deleteTenant(tenantId: string): Observable<void> {
    return this.api.delete(`${this.resourcePath}/${tenantId}`).pipe(map(() => void 0));
  }

  getSubscriptions(tenantId: string): Observable<readonly SubscriptionDto[]> {
    type ApiSub = SubscriptionDto;
    return this.api
      .get<readonly ApiSub[]>(`${this.resourcePath}/${tenantId}/subscriptions`)
      .pipe(map((arr) => (Array.isArray(arr) ? arr : [])));
  }

  createSubscription(
    tenantId: string,
    payload: CreateSubscriptionPayload,
  ): Observable<SubscriptionDto> {
    return this.api
      .post<SubscriptionDto>(`${this.resourcePath}/${tenantId}/subscriptions`, payload)
      .pipe(
        map((s: any) => ({
          subscriptionUuid: s?.subscriptionUuid ?? s?.id ?? s?.uuid ?? '',
          tenantUuid: s?.tenantUuid ?? s?.tenantId ?? tenantId,
          subscriptionName: s?.subscriptionName ?? s?.name ?? '',
          createdAt: String(s?.createdAt ?? s?.created_at ?? ''),
          updatedAt: String(s?.updatedAt ?? s?.updated_at ?? ''),
        })),
      );
  }
}
